import {
    ChevronLeft,
    ChevronRight,
    Download,
    HelpCircle,
    MoveDownRight,
    MoveUpRight,
} from 'lucide-react';
import { GoogleIcon, InstagramIcon, TikTokIcon } from 'components/Icons';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/ui/design-system/components/ui/tooltip';
import { formatAbsoluteGrowth, formatVolume } from 'utils/helpers';
import {
    getDriversSourceConfig,
    normalizeDriversSource,
} from 'utils/driversOfGrowthConfig';
import { useCallback, useMemo, useState } from 'react';
import useShowTranslations, {
    translationCSVColumn,
} from 'utils/hooks/useShowTranslations';
import { Button } from '@/ui/design-system/components/ui/button';
import Loading from 'components/Loading';
import Post from 'components/Post/Post';
import TimeSeriesKeyChipColor from 'components/DataVis/TimeSeriesKeyChipColor';
import dayjs from 'dayjs';
import { downloadItemsAsCSV } from 'utils/formatCSV';
import styled from 'styled-components';
import { useDriversOfGrowth } from 'utils/hooks/useDriversOfGrowth';
import useTableColumns from 'utils/hooks/useTableColumns';
import { withMixpanel } from 'utils/app';

import {
    ColumnsButton,
    HideAllPostsButton,
    PostButtonsWrapper,
    ShowMorePostsButton,
} from './PostButtons';
import DrawerPostComponent from './DrawerPostComponent';
import HashtagsList from './HashtagsList';

const DrawerSection = styled.div`
    padding: 0 1rem;
`;

const DrawerPostsWrapper = styled.div`
    --post-thumbnail-height: 17rem;
`;

const SOURCE_ICONS = {
    google: GoogleIcon,
    instagram: InstagramIcon,
    tiktok: TikTokIcon,
    pop_index_google: GoogleIcon,
    pop_index_instagram: InstagramIcon,
    pop_index_tiktok: TikTokIcon,
};

const SOURCE_SUMMARY_COLORS = {
    google: '#3B82F6',
    tiktok: '#F43F5E',
    instagram: '#F97316',
};

const getChangeValue = (accessors, datum) => {
    if (!(accessors && datum)) return null;
    const wowValue = accessors.wowChangeAccessor
        ? accessors.wowChangeAccessor(datum)
        : null;
    if (wowValue != null) return wowValue;

    const momValue = accessors.momChangeAccessor
        ? accessors.momChangeAccessor(datum)
        : null;
    if (momValue != null) return momValue;

    return null;
};

function DriversOfGrowthDrawer({
    selectedPointData,
    setSelectedPointData,
    accessors,
    country,
    category,
    categoryDepth,
    brand,
    dataSource = 'tiktok',
    historicalData = [],
    navigationMiddleContent = null,
    summarySourceStats = [],
    beforePrimarySection = null,
    headerPrimaryMetric = null,
    showTopChrome = true,
    additionalExports = [],
    isDatumEstimated,
    mixpanel,
}) {
    const normalizedDataSource = normalizeDriversSource(dataSource);
    const sourceConfig = getDriversSourceConfig(normalizedDataSource);
    const SourceIcon = SOURCE_ICONS[normalizedDataSource] || TikTokIcon;
    const {
        currentVolumeLabel,
        exportPrefix,
        hashtagMetricLabel,
        primaryListTitle,
        primaryColumnLabel,
        primaryItemLabelPlural = 'hashtags',
        primaryListColumns = 2,
        primaryItemMaxWidthClass = 'max-w-[180px]',
        primaryExportSuffix,
        changeDataKey,
        changeLabel,
        changePeriodLabel,
        periodRatioLabel = 'WoW',
        dateRangeType,
        supportsPosts = true,
        showFlagging = true,
        postColumns,
        postMetrics,
    } = sourceConfig;

    const date = selectedPointData
        ? dayjs(accessors.xAccessor(selectedPointData.datum)).format(
              'YYYY-MM-DD',
          )
        : null;

    const trackingMeta = {
        dataSource,
        country,
        ...(brand
            ? { brand, type: 'brand' }
            : { category, categoryDepth, type: 'category' }),
    };

    const sortedHistoricalDates = useMemo(() => {
        if (!historicalData.length || !accessors?.xAccessor) return [];
        return [...historicalData].sort(
            (a, b) =>
                dayjs(accessors.xAccessor(a)).valueOf() -
                dayjs(accessors.xAccessor(b)).valueOf(),
        );
    }, [historicalData, accessors]);

    const currentIndex = useMemo(() => {
        if (!selectedPointData || !sortedHistoricalDates.length) return -1;
        const currentDate = accessors.xAccessor(selectedPointData.datum);
        return sortedHistoricalDates.findIndex((d) =>
            dayjs(accessors.xAccessor(d)).isSame(dayjs(currentDate), 'day'),
        );
    }, [selectedPointData, sortedHistoricalDates, accessors]);

    const currentPeriodVolume =
        selectedPointData && accessors?.yAccessor
            ? accessors.yAccessor(selectedPointData.datum)
            : null;

    const changeValue = selectedPointData
        ? getChangeValue(accessors, selectedPointData.datum)
        : null;
    const resolvedCurrentVolume =
        headerPrimaryMetric?.value ?? currentPeriodVolume;
    const resolvedChangeValue = headerPrimaryMetric?.change ?? changeValue;
    const resolvedCurrentVolumeLabel =
        headerPrimaryMetric?.label ?? currentVolumeLabel;

    const isEstimated =
        typeof isDatumEstimated === 'function' && selectedPointData
            ? !!isDatumEstimated(selectedPointData.datum)
            : false;

    const isNegativeWeek =
        resolvedChangeValue != null ? resolvedChangeValue < 0 : false;

    const driversQuery = useDriversOfGrowth({
        country,
        category,
        brand,
        date,
        enabled: !!selectedPointData,
        isNegativeWeek,
        dataSource: normalizedDataSource,
    });

    const { hashtagsData, postsData } = driversQuery;
    const [translationLanguage, setTranslationLanguage] = useState(null);
    const showTranslations = useShowTranslations({
        rows: hashtagsData,
        countries: [country],
    });
    const defaultSortOption = useMemo(
        () => ({ sortBy: changeDataKey }),
        [changeDataKey],
    );

    const { visibleOrderedMetrics, columnsMenu } = useTableColumns({
        tableName: `drivers-of-growth-posts-${normalizedDataSource}`,
        metrics: postMetrics,
        buttonProps: {
            as: ColumnsButton,
        },
    });

    const canGoPrevious = currentIndex > 0;
    const canGoNext =
        currentIndex >= 0 && currentIndex < sortedHistoricalDates.length - 1;

    const handlePrevious = useCallback(() => {
        if (canGoPrevious && setSelectedPointData) {
            mixpanel?.track('Click', {
                context: 'DRIVERS_OF_GROWTH_PREVIOUS',
                ...trackingMeta,
            });
            const prevDatum = sortedHistoricalDates[currentIndex - 1];
            setSelectedPointData({ datum: prevDatum });
        }
    }, [
        canGoPrevious,
        currentIndex,
        sortedHistoricalDates,
        setSelectedPointData,
        mixpanel,
        trackingMeta,
    ]);

    const handleNext = useCallback(() => {
        if (canGoNext && setSelectedPointData) {
            mixpanel?.track('Click', {
                context: 'DRIVERS_OF_GROWTH_NEXT',
                ...trackingMeta,
            });
            const nextDatum = sortedHistoricalDates[currentIndex + 1];
            setSelectedPointData({ datum: nextDatum });
        }
    }, [
        canGoNext,
        currentIndex,
        sortedHistoricalDates,
        setSelectedPointData,
        mixpanel,
        trackingMeta,
    ]);

    const dateRangeLabel = useMemo(() => {
        if (!selectedPointData) return '';
        const endDate = dayjs(accessors.xAccessor(selectedPointData.datum));
        if (dateRangeType === 'monthly') {
            return endDate.format('MMM YYYY');
        }
        const startDate = endDate.subtract(6, 'day');
        const year = endDate.format('YYYY');
        if (startDate.month() === endDate.month()) {
            return `${startDate.format('MMM D')}-${endDate.format('D')}, ${year}`;
        }
        return `${startDate.format('MMM D')} - ${endDate.format('MMM D')}, ${year}`;
    }, [selectedPointData, accessors, dateRangeType]);

    const hashtagsLoading = driversQuery.hashtagsQuery.isLoading;
    const postsLoading = supportsPosts && driversQuery.postsQuery.isLoading;
    const hashtagsHasData = hashtagsData && hashtagsData.length > 0;
    const postsHasData = supportsPosts && postsData && postsData.length > 0;
    const hasNoData =
        !hashtagsLoading &&
        !postsLoading &&
        !hashtagsHasData &&
        (!supportsPosts || !postsHasData);
    const hasSummarySourceStats =
        Array.isArray(summarySourceStats) && summarySourceStats.length > 0;

    const primaryColumns = useMemo(
        () => [
            { key: 'kw', label: primaryColumnLabel },
            { key: 'volume', label: changeLabel },
            ...(showTranslations ? [translationCSVColumn] : []),
        ],
        [primaryColumnLabel, changeLabel, showTranslations],
    );

    const handleToggleTranslations = useCallback(() => {
        mixpanel?.track('Click', {
            context: 'DRIVERS_OF_GROWTH_TRANSLATIONS',
            showTranslations: !translationLanguage,
            ...trackingMeta,
        });
        setTranslationLanguage(translationLanguage ? null : 'en');
    }, [translationLanguage, mixpanel, trackingMeta]);

    const handleExport = useCallback(() => {
        mixpanel?.track('Click', {
            context: 'DRIVERS_OF_GROWTH_CSV_DOWNLOAD',
            ...trackingMeta,
        });
        const dateStr = date ? dayjs(date).format('YYYY-MM-DD') : 'unknown';
        const hashtagsDataForExport = (hashtagsData || []).filter(
            (item) => item && item.kw && item.volume != null,
        );
        const shouldUsePopIndexHashtagTitleRow =
            hasSummarySourceStats &&
            (normalizedDataSource === 'pop_index_tiktok' ||
                normalizedDataSource === 'pop_index_instagram');

        if (hashtagsDataForExport.length > 0) {
            const hashtagsFilename = `${exportPrefix}-${primaryExportSuffix}-${dateStr}`;
            downloadItemsAsCSV(
                hashtagsFilename,
                hashtagsDataForExport,
                primaryColumns,
                shouldUsePopIndexHashtagTitleRow
                    ? { titleRow: hashtagsFilename }
                    : undefined,
            );
        }

        if (supportsPosts && postsData && postsData.length > 0) {
            downloadItemsAsCSV(
                `${exportPrefix}-post-${dateStr}`,
                postsData,
                postColumns,
            );
        }

        (additionalExports || []).forEach((exportItem) => {
            if (!exportItem) return;
            const rows = (exportItem.data || []).filter(Boolean);
            if (!rows.length) return;
            const filename = exportItem.filename || `export-${dateStr}`;
            const columns = exportItem.columns || [];
            const shouldUseTitleRow =
                filename.startsWith('pop-index-') &&
                filename.includes('-hashtags-');
            downloadItemsAsCSV(
                filename,
                rows,
                columns,
                shouldUseTitleRow ? { titleRow: filename } : undefined,
            );
        });
    }, [
        date,
        hashtagsData,
        postsData,
        mixpanel,
        trackingMeta,
        postColumns,
        exportPrefix,
        primaryExportSuffix,
        primaryColumns,
        supportsPosts,
        additionalExports,
        normalizedDataSource,
        hasSummarySourceStats,
    ]);

    return (
        <TooltipProvider>
            <div className="flex flex-col pb-24">
                {showTopChrome && (
                    <div className="flex items-center justify-between p-4">
                        <div className="flex items-center gap-2">
                            <SourceIcon className="size-4 text-neutral-500" />
                            <span className="font-semibold text-base text-card-foreground">
                                Growth Drivers {dateRangeLabel}
                            </span>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button className="text-neutral-500 hover:text-neutral-400">
                                        <HelpCircle className="size-4" />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>
                                        {primaryListTitle.toLowerCase()}{' '}
                                        {supportsPosts ? ' and posts ' : ' '}
                                        with the largest{' '}
                                        {isNegativeWeek
                                            ? 'negative'
                                            : 'positive'}{' '}
                                        {changePeriodLabel} change.
                                    </p>
                                </TooltipContent>
                            </Tooltip>
                        </div>
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={handleExport}
                            disabled={
                                hasNoData || hashtagsLoading || postsLoading
                            }
                        >
                            <Download className="size-4" />
                            Export Data
                        </Button>
                    </div>
                )}

                {showTopChrome &&
                    (resolvedCurrentVolume != null ||
                        hasSummarySourceStats) && (
                        <div className="flex items-start justify-between gap-4 pl-10 pr-4 pb-4 -mt-2">
                            <div className="flex items-start gap-6">
                                {resolvedCurrentVolume != null && (
                                    <div className="flex flex-col">
                                        <span
                                            className={`text-lg font-semibold uppercase ${isEstimated ? 'italic opacity-60 text-neutral-900' : ''}`}
                                        >
                                            {formatVolume(
                                                resolvedCurrentVolume,
                                            )}
                                        </span>
                                        <span
                                            className={`text-xs ${isEstimated ? 'italic opacity-60 text-neutral-900' : 'text-muted-foreground'}`}
                                        >
                                            {resolvedCurrentVolumeLabel}
                                        </span>
                                    </div>
                                )}
                                {resolvedChangeValue != null && (
                                    <div className="flex flex-col">
                                        <span
                                            className={`text-lg font-semibold flex items-center gap-0.5 ${isNegativeWeek ? 'text-red-600' : 'text-green-600'}`}
                                        >
                                            <span className="uppercase">
                                                {formatAbsoluteGrowth(
                                                    resolvedChangeValue,
                                                )}
                                            </span>
                                            {isNegativeWeek ? (
                                                <MoveDownRight className="size-4" />
                                            ) : (
                                                <MoveUpRight className="size-4" />
                                            )}
                                        </span>
                                        <span className="text-xs text-muted-foreground">
                                            {changeLabel}
                                        </span>
                                    </div>
                                )}
                            </div>
                            {hasSummarySourceStats && (
                                <div className="min-w-[320px] rounded-md border border-neutral-200 bg-white overflow-hidden">
                                    <div
                                        className="grid divide-x divide-neutral-200"
                                        style={{
                                            gridTemplateColumns: `repeat(${summarySourceStats.length}, minmax(0, 1fr))`,
                                        }}
                                    >
                                        {summarySourceStats.map((stat) => {
                                            const hasIncrease =
                                                stat?.increase != null;
                                            const isPositive =
                                                (stat?.increase ?? 0) >= 0;
                                            return (
                                                <div
                                                    key={stat.id}
                                                    className="px-3 py-2"
                                                >
                                                    <div className="flex items-center gap-1.5">
                                                        <TimeSeriesKeyChipColor
                                                            $color={
                                                                SOURCE_SUMMARY_COLORS[
                                                                    stat.id
                                                                ] || '#A3A3A3'
                                                            }
                                                            style={{
                                                                minWidth:
                                                                    '0.2rem',
                                                                width: '0.2rem',
                                                                minHeight:
                                                                    '0.95rem',
                                                                height: '0.95rem',
                                                                marginRight:
                                                                    '0',
                                                                borderRadius:
                                                                    '9999px',
                                                            }}
                                                        />
                                                        <span
                                                            className={`text-base font-semibold uppercase ${
                                                                !hasIncrease
                                                                    ? 'text-muted-foreground'
                                                                    : isPositive
                                                                      ? 'text-green-600'
                                                                      : 'text-red-600'
                                                            }`}
                                                        >
                                                            {hasIncrease
                                                                ? formatAbsoluteGrowth(
                                                                      stat.increase,
                                                                  )
                                                                : 'N/A'}
                                                        </span>
                                                    </div>
                                                    <div className="text-[11px] text-muted-foreground mt-1">
                                                        {stat.label}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                {showTopChrome && isEstimated && (
                    <div className="pl-10 pr-4 pb-4 -mt-2 text-[0.5625rem] italic text-neutral-900 opacity-60">
                        {`* ${sourceConfig.sourceName} data estimated for this period`}
                    </div>
                )}

                {showTopChrome && (
                    <div
                        className={`p-4 ${navigationMiddleContent ? 'grid grid-cols-[auto_1fr_auto] gap-3 items-center' : 'flex items-center justify-between'}`}
                    >
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={handlePrevious}
                            disabled={!canGoPrevious}
                        >
                            <ChevronLeft className="size-4" />
                            Previous
                        </Button>
                        {navigationMiddleContent ? (
                            <div className="flex items-center justify-center">
                                {navigationMiddleContent}
                            </div>
                        ) : null}
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={handleNext}
                            disabled={!canGoNext}
                        >
                            Next
                            <ChevronRight className="size-4" />
                        </Button>
                    </div>
                )}

                <>
                    {beforePrimarySection}
                    <div className="flex flex-col">
                        <div className="flex items-center gap-2 p-4">
                            <SourceIcon className="size-4 text-neutral-500" />
                            <span className="font-semibold text-base text-card-foreground">
                                {primaryListTitle}
                            </span>
                            <span className="text-sm font-medium text-muted-foreground">
                                {changeLabel}
                            </span>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button className="text-neutral-500 hover:text-neutral-400">
                                        <HelpCircle className="size-4" />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>
                                        {primaryListTitle} ranked by{' '}
                                        {changePeriodLabel} change.
                                    </p>
                                </TooltipContent>
                            </Tooltip>
                            {showTranslations && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={handleToggleTranslations}
                                    className="ml-auto text-primary"
                                >
                                    {translationLanguage
                                        ? 'Hide Translations'
                                        : 'Show Translations'}
                                </Button>
                            )}
                        </div>

                        <DrawerSection>
                            {hashtagsLoading ? (
                                <div className="flex items-center justify-center py-6">
                                    <Loading />
                                </div>
                            ) : hashtagsHasData ? (
                                <HashtagsList
                                    hashtags={hashtagsData}
                                    displayQty={6}
                                    country={country}
                                    category={category}
                                    brand={brand}
                                    metricLabel={hashtagMetricLabel}
                                    enableFlagging={showFlagging}
                                    showMoreItemLabel={primaryItemLabelPlural}
                                    listColumns={primaryListColumns}
                                    itemMaxWidthClass={primaryItemMaxWidthClass}
                                    translationLanguage={
                                        showTranslations
                                            ? translationLanguage
                                            : null
                                    }
                                />
                            ) : (
                                <div className="text-center text-muted-foreground text-sm py-6">
                                    No {primaryItemLabelPlural} for this date.
                                </div>
                            )}
                        </DrawerSection>
                    </div>

                    {supportsPosts && (
                        <div className="flex flex-col mt-4">
                            <div className="flex items-center justify-between p-4">
                                <div className="flex items-center gap-2">
                                    <SourceIcon className="size-4 text-neutral-500" />
                                    <span className="font-semibold text-base text-card-foreground">
                                        Top Posts
                                    </span>
                                    <span className="text-sm font-medium text-muted-foreground">
                                        {changeLabel}
                                    </span>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <button className="text-neutral-500 hover:text-neutral-400">
                                                <HelpCircle className="size-4" />
                                            </button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>
                                                Posts with the largest{' '}
                                                {isNegativeWeek
                                                    ? 'negative'
                                                    : 'positive'}{' '}
                                                {changePeriodLabel} change.
                                            </p>
                                        </TooltipContent>
                                    </Tooltip>
                                </div>
                                {columnsMenu}
                            </div>

                            <DrawerSection>
                                <DrawerPostsWrapper>
                                    <Post
                                        posts={postsData || []}
                                        isLoading={postsLoading}
                                        isError={
                                            driversQuery.postsQuery.isError
                                        }
                                        displayQty={6}
                                        sortOption={defaultSortOption}
                                        visibleOrderedMetrics={
                                            visibleOrderedMetrics
                                        }
                                        PostComponent={(props) => (
                                            <DrawerPostComponent
                                                {...props}
                                                dataSource={
                                                    normalizedDataSource
                                                }
                                                changeLabel={changeLabel}
                                                periodRatioLabel={
                                                    periodRatioLabel
                                                }
                                            />
                                        )}
                                        NoDataMessage={() => (
                                            <>No posts for this date.</>
                                        )}
                                        ShowMoreButton={ShowMorePostsButton}
                                        HideAllButton={HideAllPostsButton}
                                        ButtonsWrapper={PostButtonsWrapper}
                                    />
                                </DrawerPostsWrapper>
                            </DrawerSection>
                        </div>
                    )}
                </>
            </div>
        </TooltipProvider>
    );
}

export default withMixpanel(DriversOfGrowthDrawer);
